import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

export interface DestinationFormData {
  destinationId: string;
  type: string;
  connectionString: string;
  tableName: string;
  schemaName: string;
  sqlBeforeScript: string;
  sqlAfterScript: string;
  scriptTimeout: string;
  isEnabled: boolean;
}

interface SftpDestinationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (destination: DestinationFormData) => void;
  destination?: DestinationFormData;
  mode: 'create' | 'edit';
}

const initialFormData: DestinationFormData = {
  destinationId: '',
  type: 'MSSQL',
  connectionString: '',
  tableName: '',
  schemaName: 'dbo',
  sqlBeforeScript: '',
  sqlAfterScript: '',
  scriptTimeout: '5 minutes',
  isEnabled: true,
};

export const SftpDestinationDialog: React.FC<SftpDestinationDialogProps> = ({
  open,
  onClose,
  onSave,
  destination,
  mode,
}) => {
  const [formData, setFormData] = useState<DestinationFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === 'edit' && destination) {
      setFormData(destination);
    } else if (mode === 'create') {
      const generatedId = uuidv4().replace(/-/g, '');
      setFormData({
        ...initialFormData,
        destinationId: generatedId,
      });
    }
  }, [mode, destination, open]);

  const handleFieldChange = (field: keyof DestinationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === 'create' && !formData.connectionString) newErrors.connectionString = 'Connection String is required';
    if (!formData.tableName) newErrors.tableName = 'Table Name is required';
    if (!formData.schemaName) newErrors.schemaName = 'Schema Name is required';
    if (!formData.scriptTimeout || formData.scriptTimeout.trim() === '') {
      newErrors.scriptTimeout = 'Script Timeout is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Add Destination' : 'Edit Destination'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">Destination Configuration</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Database Type</InputLabel>
              <Select
                value={formData.type}
                label="Database Type"
                onChange={(e) => handleFieldChange('type', e.target.value)}
              >
                <MenuItem value="MSSQL">MS SQL Server</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEnabled}
                  onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Connection String"
              value={formData.connectionString}
              onChange={(e) => handleFieldChange('connectionString', e.target.value)}
              error={!!errors.connectionString}
              helperText={errors.connectionString || (mode === 'edit' ? 'Leave blank to keep the current connection string' : undefined)}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Schema Name"
              value={formData.schemaName}
              onChange={(e) => handleFieldChange('schemaName', e.target.value)}
              error={!!errors.schemaName}
              helperText={errors.schemaName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Table Name"
              value={formData.tableName}
              onChange={(e) => handleFieldChange('tableName', e.target.value)}
              error={!!errors.tableName}
              helperText={errors.tableName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="SQL Before Script (Optional)"
              value={formData.sqlBeforeScript}
              onChange={(e) => handleFieldChange('sqlBeforeScript', e.target.value)}
              multiline
              rows={3}
              helperText="SQL script to run before data import"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="SQL After Script (Optional)"
              value={formData.sqlAfterScript}
              onChange={(e) => handleFieldChange('sqlAfterScript', e.target.value)}
              multiline
              rows={3}
              helperText="SQL script to run after data import"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Script Timeout"
              value={formData.scriptTimeout}
              onChange={(e) => handleFieldChange('scriptTimeout', e.target.value)}
              error={!!errors.scriptTimeout}
              helperText={errors.scriptTimeout || "Timeout for SQL before/after scripts (e.g. '30 seconds', '5 minutes', '1 hour')"}
              placeholder="5 minutes"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};